import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
	SortingState,
	getSortedRowModel,
} from '@tanstack/react-table';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { useState } from 'react';
import { openDialog } from '@/redux/slices/pageSlice';
import { useAppDispatch } from '@/lib/hooks/hooks';
import DocumentWithExclamation from '@/components/ui/icons/new/DocumentWithExclamation';

interface DocumentTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
}

export function FormativeDocumentTable<TData, TValue>({
	columns,
	data,
}: DocumentTableProps<TData, TValue>) {
	const dispatch = useAppDispatch();
	const [sorting, setSorting] = useState<SortingState>([]);
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});

	return (
		<div className="w-full">
			<Table parentClassName="max-h-[320px] shadow-main">
				<TableHeader className="sticky top-0 rounded-tl-2xl rounded-tr-2xl bg-blackSecondary">
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<TableHead key={header.id} className="p-4 text-lg text-black">
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody className="overflow-y-auto text-base text-black bg-white">
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map((row) => (
							<TableRow
								className="transition hover:bg-lightBackground"
								key={row.id}
								data-state={row.getIsSelected() && 'selected'}
							>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-28 text-center">
								<div className="flex flex-col gap-4 py-6">
									<div className="flex items-center justify-center">
										<DocumentWithExclamation className="h-28" />
									</div>
									<div className="flex items-center justify-center text-black font-bold">
										You have not added any essential documents yet.
										<a
											onClick={() =>
												dispatch(
													openDialog({
														type: 'addDocuments',
														addDocumentType: 'attachFormativeDocument',
													})
												)
											}
											className="lowercase font-bold text-primary hover:text-secondary pl-0.75"
										>
											click to add documents
										</a>
									</div>
								</div>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}
